import { Component, OnDestroy, OnInit , DestroyRef, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { TableModule } from 'primeng/table';
import { Button } from 'primeng/button';
import { Dialog } from 'primeng/dialog';
import { Tooltip } from 'primeng/tooltip';
import { Tag } from 'primeng/tag';
import { MultiSelect } from 'primeng/multiselect';
import { MessageService } from 'primeng/api';
import { forkJoin, catchError, of } from 'rxjs';
import { ProjectService } from '../../services/project.service';
import { PaymentTrackingItemService, PaymentTrackingItem } from '../../services/payment-tracking-item.service';
import { AllocationService } from '../../services/allocation.service';
import { YearMonthFilterService } from '../../services/year-month-filter.service';
import { AuthService } from '../../services/auth.service';
import { EmployeeService } from '../../services/employee.service';
import { Project } from '../../models/project.model';
import { Employee } from '../../models/employee.model';
import { Allocation, InvoiceType } from '../../models/allocation.model';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import { PageHeaderSearchActionService } from '../../services/page-header-search-action.service';

interface ProjectInvoiceData {
  project: Project;
  invoices: PaymentTrackingItem[];
  monthlyData: { [month: number]: { totalPercentage: number; count: number; invoices: PaymentTrackingItem[] } };
  invoiceType?: InvoiceType;
  invoiceTypeName?: string;
  /**
   * Milestones that carry no usable date at all (neither Target Date nor Invoiced Date) and so
   * cannot be placed in any month column. Surfaced as a warning on the row rather than dropped
   * silently - otherwise the row's twelve dashes read as "nothing to bill" when the truth is
   * "we don't know when". See `bucketingDate`.
   */
  undatedCount: number;
}

interface CalendarDay {
  day: number;
  date: Date;
  invoices: PaymentTrackingItem[];
  totalPercentage: number;
  count: number;
  status: 'green' | 'none';
}

@Component({
  selector: 'app-invoice-planning-grid',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    TableModule,
    Button,
    Dialog,
    Tooltip,
    Tag,
    MultiSelect
  ],
  templateUrl: './invoice-planning-grid.component.html',
  styleUrl: './invoice-planning-grid.component.scss'
})
export class InvoicePlanningGridComponent implements OnInit, OnDestroy {
  private destroyRef = inject(DestroyRef);
  projects: Project[] = [];
  projectInvoiceData: ProjectInvoiceData[] = [];
  filteredProjects: ProjectInvoiceData[] = [];
  private searchText = '';
  
  loading: boolean = true;
  selectedYear: number = new Date().getFullYear();
  
  // Calendar dialog
  showCalendarDialog: boolean = false;
  selectedMonthForCalendar: number = new Date().getMonth();
  selectedYearForCalendar: number = new Date().getFullYear();
  calendarDays: CalendarDay[] = [];
  selectedDate: Date | null = null;
  
  // Expose InvoiceType enum to template
  InvoiceType = InvoiceType;
  
  months = [
    { label: 'Jan', value: 0, fullName: 'January' },
    { label: 'Feb', value: 1, fullName: 'February' },
    { label: 'Mar', value: 2, fullName: 'March' },
    { label: 'Apr', value: 3, fullName: 'April' },
    { label: 'May', value: 4, fullName: 'May' },
    { label: 'Jun', value: 5, fullName: 'June' },
    { label: 'Jul', value: 6, fullName: 'July' },
    { label: 'Aug', value: 7, fullName: 'August' },
    { label: 'Sep', value: 8, fullName: 'September' },
    { label: 'Oct', value: 9, fullName: 'October' },
    { label: 'Nov', value: 10, fullName: 'November' },
    { label: 'Dec', value: 11, fullName: 'December' }
  ];

  allocations: Allocation[] = [];
  selectedMonth: number = new Date().getMonth() + 1; // 1-12 for Month enum

  // Filter options
  invoiceTypeOptions: { label: string; value: number }[] = [
    { label: 'Monthly', value: InvoiceType.Monthly },
    { label: 'Project Milestone', value: InvoiceType.ProjectMilestone }
  ];
  selectedInvoiceTypes: number[] = [];

  invoiceStatusOptions: { label: string; value: string }[] = [
    { label: 'Open', value: 'Open' },
    { label: 'Paid', value: 'Paid' },
    { label: 'Ready for Invoice', value: 'Ready for Invoice' },
    { label: 'Invoiced', value: 'Invoiced' }
  ];
  selectedInvoiceStatuses: string[] = [];

  // PM / Service Group / Account Manager / Client filters
  selectedPMs: string[] = [];
  selectedServiceGroups: string[] = [];
  selectedAccountManagers: string[] = [];
  selectedClients: string[] = [];

  pmFilterOptions: { label: string; value: string }[] = [];
  serviceGroupFilterOptions: { label: string; value: string }[] = [];
  accountManagerFilterOptions: { label: string; value: string }[] = [];
  clientFilterOptions: { label: string; value: string }[] = [];

  constructor(
    private projectService: ProjectService,
    private paymentTrackingService: PaymentTrackingItemService,
    private allocationService: AllocationService,
    private employeeService: EmployeeService,
    private messageService: MessageService,
    private router: Router,
    public pageHeaderService: PageHeaderSearchActionService,
    private yearMonthFilterService: YearMonthFilterService,
    private authService: AuthService
  ) {
    this.selectedYear = this.yearMonthFilterService.currentYear;
  }

  ngOnInit() {
    // Sync year from the shared header year dropdown
    this.yearMonthFilterService.year
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(year => {
        if (year !== this.selectedYear) {
          this.selectedYear = year;
          this.loadData();
        }
      });

    // Header integration: filter toggle + search + Export Excel
    this.pageHeaderService.setCanShowFilterToggle(true);
    this.pageHeaderService.setPlaceholder('Search by project name...');
    this.pageHeaderService.setCanShowPrimaryAction(false);
    this.pageHeaderService.setCanShowExport(true);
    this.pageHeaderService.setExportDisabled(true);
    this.pageHeaderService.searchText
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((text) => {
        this.searchText = (text ?? '').trim().toLowerCase();
        this.applySearch();
      });
    this.pageHeaderService.exportClick
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.exportToExcel());

    this.loadData();
  }

  ngOnDestroy(): void {
    this.pageHeaderService.clearPageConfig();
  }

  loadData() {
    this.loading = true;

    forkJoin({
      projects: this.projectService.getAllProjects().pipe(
        catchError(error => {
          console.error('Error loading projects:', error);
          this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Failed to load projects.' });
          return of([] as Project[]);
        })
      ),
      employees: this.employeeService.getAllEmployees().pipe(
        catchError(() => of([] as Employee[]))
      )
    }).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: ({ projects, employees }) => {
        // Only include active projects with Monthly or Project Milestone invoicing type
        let activeProjects = projects.filter(p =>
          p.isActive &&
          (p.invoiceType === InvoiceType.Monthly || p.invoiceType === InvoiceType.ProjectMilestone)
        );

        // PM (non-admin, non-finance): only show their own projects
        const user = this.authService.getUser();
        if (user && !user.isAdmin && !user.isFinance && user.isPM) {
          const pmEmployee = employees.find(
            e => e.email?.toLowerCase() === user.email?.toLowerCase()
          );
          if (pmEmployee) {
            activeProjects = activeProjects.filter(p => p.managerId === pmEmployee.id);
            // Fallback: if managerId matches nothing, try managerName
            if (activeProjects.length === 0) {
              const pmNameLower = (user.displayName ?? '').toLowerCase().trim();
              activeProjects = projects.filter(p =>
                p.isActive &&
                (p.invoiceType === InvoiceType.Monthly || p.invoiceType === InvoiceType.ProjectMilestone) &&
                (p.managerName ?? '').toLowerCase().trim() === pmNameLower
              );
            }
          } else {
            // No employee record found by email — fall back to display name matching
            const pmNameLower = (user.displayName ?? '').toLowerCase().trim();
            activeProjects = activeProjects.filter(p =>
              (p.managerName ?? '').toLowerCase().trim() === pmNameLower
            );
          }
        }

        this.projects = activeProjects;

        if (this.projects.length === 0) {
          this.allocations = [];
          this.projectInvoiceData = [];
          this.filteredProjects = [];
          this.loading = false;
          this.pageHeaderService.setExportDisabled(true);
          return;
        }

        // Load allocations for the selected year/month
        this.allocationService.getAllAllocations(this.selectedYear, this.selectedMonth).pipe(
          catchError(() => of([] as Allocation[]))
        ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
          next: (allocations) => {
            this.allocations = allocations;
            this.loadInvoiceData();
          },
          error: () => {
            this.allocations = [];
            this.loadInvoiceData();
          }
        });
      },
      error: (error) => {
        console.error('Error loading data:', error);
        this.loading = false;
      }
    });
  }

  loadInvoiceData() {
    const invoicePromises = this.projects.map(project =>
      this.paymentTrackingService.getByProjectId(project.id).pipe(
        catchError(error => {
          console.error(`Error loading invoices for project ${project.id}:`, error);
          return of([] as PaymentTrackingItem[]);
        })
      )
    );

    forkJoin(invoicePromises).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (allInvoices) => {
        this.projectInvoiceData = this.projects.map((project, index) => {
          const allItems = allInvoices[index] || [];

          // Keep every milestone that can be placed in a month. Previously this kept only
          // items with a Target Date, which silently discarded 38% of production rows -
          // including invoices already raised or collected. See `bucketingDate`.
          const invoices = allItems.filter(inv => this.bucketingDate(inv) !== null);
          const undatedCount = allItems.length - invoices.length;

          // Use invoice type from Project (Projects Inventory), not from allocations
          const invoiceType = project.invoiceType ?? InvoiceType.NotAvailable;

          const monthlyData: { [month: number]: { totalPercentage: number; count: number; invoices: PaymentTrackingItem[] } } = {};

          // Initialize all months
          for (let i = 0; i < 12; i++) {
            monthlyData[i] = { totalPercentage: 0, count: 0, invoices: [] };
          }

          // Group invoices by month
          invoices.forEach(invoice => {
            const parts = this.bucketingDate(invoice);
            if (parts && parts.year === this.selectedYear) {
              const month = parts.month;
              // Guard the null/undefined case - a single null would make the whole
              // month bucket NaN and blank out the cell.
              monthlyData[month].totalPercentage += invoice.percentage ?? 0;
              monthlyData[month].count++;
              monthlyData[month].invoices.push(invoice);
            }
          });

          return {
            project,
            invoices,
            monthlyData,
            invoiceType,
            invoiceTypeName: this.getInvoiceTypeName(invoiceType),
            undatedCount
          };
        });
        
        this.buildFilterOptions();
        this.applySearch();
        this.loading = false;
        this.pageHeaderService.setExportDisabled(this.filteredProjects.length === 0);
      },
      error: (error) => {
        console.error('Error loading invoice data:', error);
        this.loading = false;
        this.pageHeaderService.setExportDisabled(true);
      }
    });
  }

  /**
   * The year/month a milestone belongs to, or null if it cannot be placed.
   *
   * Target Date is the planning date and stays the primary key. Invoiced Date is the fallback:
   * an invoice that has actually been raised always has one, and billed revenue belongs on this
   * screen whether or not anyone filled in the plan date first. Without the fallback, 91 of
   * production's 378 milestones - 70 of them already Invoiced or Paid - were discarded here and
   * never reached a month column.
   *
   * Parsed off the string rather than via `new Date(...).getMonth()`. The API serializes these
   * as date-only ISO strings; handing one to the Date constructor can be read as UTC midnight and
   * then shifted back a day in a negative-offset timezone - which would move the 69 production
   * rows dated the 1st of a month into the previous month, and the 6 dated 1 January out of the
   * selected year entirely.
   */
  private bucketingDate(invoice: PaymentTrackingItem): { year: number; month: number } | null {
    const raw = invoice.targetDate ?? invoice.invoicedDate;
    if (!raw) return null;

    const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(raw);
    if (match) {
      return { year: Number(match[1]), month: Number(match[2]) - 1 };
    }

    // Non-ISO shape - fall back to Date parsing rather than dropping the row.
    const parsed = new Date(raw);
    return Number.isNaN(parsed.getTime())
      ? null
      : { year: parsed.getFullYear(), month: parsed.getMonth() };
  }

  /** True when the month figure for this milestone came from Invoiced Date, not Target Date. */
  private isFallbackDated(invoice: PaymentTrackingItem): boolean {
    return !invoice.targetDate && !!invoice.invoicedDate;
  }

  private applySearch(): void {
    let filtered = this.projectInvoiceData;

    // Invoice type filter
    if (this.selectedInvoiceTypes?.length > 0) {
      filtered = filtered.filter(p => this.selectedInvoiceTypes.includes(p.invoiceType!));
    }

    // Invoice status filter — only show projects that have invoices matching selected statuses.
    // Compared case/whitespace-insensitively to match getInvoiceStatusClass: production still
    // holds legacy rows whose status predates the current validation, and an exact string
    // comparison drops them out of every filtered view.
    if (this.selectedInvoiceStatuses?.length > 0) {
      const wanted = new Set(this.selectedInvoiceStatuses.map(s => s.trim().toLowerCase()));
      filtered = filtered.filter(p =>
        p.invoices.some(inv => wanted.has((inv.invoicingStatus ?? '').trim().toLowerCase()))
      );
    }

    if (this.selectedPMs?.length > 0) {
      filtered = filtered.filter(p => this.selectedPMs.includes(p.project.managerId ?? ''));
    }

    if (this.selectedServiceGroups?.length > 0) {
      filtered = filtered.filter(p => this.selectedServiceGroups.includes(p.project.serviceGroupId ?? ''));
    }

    if (this.selectedAccountManagers?.length > 0) {
      filtered = filtered.filter(p => this.selectedAccountManagers.includes(p.project.accountManagerId ?? ''));
    }

    if (this.selectedClients?.length > 0) {
      filtered = filtered.filter(p => this.selectedClients.includes(p.project.clientId ?? ''));
    }

    // Text search
    const term = this.searchText;
    if (term) {
      filtered = filtered.filter(p =>
        (p.project?.name ?? '').toLowerCase().includes(term)
      );
    }

    this.filteredProjects = filtered;
    this.pageHeaderService.setExportDisabled(this.loading || this.filteredProjects.length === 0);
  }

  private buildFilterOptions(): void {
    const unique = <T>(arr: (T | undefined | null)[]): T[] =>
      [...new Set(arr.filter((v): v is T => v != null && v !== ''))].sort() as T[];

    this.pmFilterOptions = unique(this.projectInvoiceData.map(p => p.project.managerId))
      .map(id => ({
        label: this.projectInvoiceData.find(p => p.project.managerId === id)?.project.managerName ?? id,
        value: id
      }));

    this.serviceGroupFilterOptions = unique(this.projectInvoiceData.map(p => p.project.serviceGroupId))
      .map(id => ({
        label: this.projectInvoiceData.find(p => p.project.serviceGroupId === id)?.project.serviceGroupName ?? id,
        value: id
      }));

    this.accountManagerFilterOptions = unique(this.projectInvoiceData.map(p => p.project.accountManagerId))
      .map(id => ({
        label: this.projectInvoiceData.find(p => p.project.accountManagerId === id)?.project.accountManagerName ?? id,
        value: id
      }));

    this.clientFilterOptions = unique(this.projectInvoiceData.map(p => p.project.clientId))
      .map(id => ({
        label: this.projectInvoiceData.find(p => p.project.clientId === id)?.project.clientName ?? id,
        value: id
      }));
  }

  onInvoiceTypeFilterChange(): void {
    this.applySearch();
  }

  onInvoiceStatusFilterChange(): void {
    this.applySearch();
  }

  onPMFilterChange(): void { this.applySearch(); }
  onServiceGroupFilterChange(): void { this.applySearch(); }
  onAccountManagerFilterChange(): void { this.applySearch(); }
  onClientFilterChange(): void { this.applySearch(); }

  hasActiveFilters(): boolean {
    return (this.selectedInvoiceTypes?.length ?? 0) > 0 ||
           (this.selectedInvoiceStatuses?.length ?? 0) > 0 ||
           (this.selectedPMs?.length ?? 0) > 0 ||
           (this.selectedServiceGroups?.length ?? 0) > 0 ||
           (this.selectedAccountManagers?.length ?? 0) > 0 ||
           (this.selectedClients?.length ?? 0) > 0;
  }

  clearFilters(): void {
    this.selectedInvoiceTypes = [];
    this.selectedInvoiceStatuses = [];
    this.selectedPMs = [];
    this.selectedServiceGroups = [];
    this.selectedAccountManagers = [];
    this.selectedClients = [];
    this.applySearch();
  }

  getInvoiceTypeName(invoiceType: InvoiceType): string {
    switch (invoiceType) {
      case InvoiceType.Monthly:
        return 'Monthly';
      case InvoiceType.ProjectMilestone:
        return 'Project Milestone';
      case InvoiceType.NotAvailable:
        return 'N/A';
      default:
        return 'Unknown';
    }
  }

  getInvoiceTypeSeverity(invoiceType: InvoiceType): 'success' | 'secondary' | 'info' | 'warn' | 'danger' | 'contrast' {
    switch (invoiceType) {
      case InvoiceType.Monthly:
        return 'info';
      case InvoiceType.ProjectMilestone:
        return 'success';
      case InvoiceType.NotAvailable:
        return 'secondary';
      default:
        return 'secondary';
    }
  }

  /** Returns CSS class for invoice status color coding. */
  getInvoiceStatusClass(invoice: PaymentTrackingItem): string {
    const status = (invoice.invoicingStatus ?? '').trim().toLowerCase();
    if (status === 'paid') return 'inv-paid';
    if (status === 'ready for invoice') return 'inv-ready';
    if (status === 'invoiced') return 'inv-invoiced';
    // Open or any other status — compare the bucketing month against the current month, so a
    // milestone dated this month is not flagged overdue partway through it.
    const parts = this.bucketingDate(invoice);
    if (parts) {
      const now = new Date();
      const elapsed = (now.getFullYear() - parts.year) * 12 + (now.getMonth() - parts.month);
      if (elapsed > 0) return 'inv-open-overdue';
    }
    return 'inv-open-future';
  }

  /** Returns the dominant status class for a month cell (priority: overdue > open > ready/invoiced > paid). */
  getMonthCellClass(projectData: ProjectInvoiceData, month: number): string {
    const data = projectData.monthlyData[month];
    if (!data || data.count === 0) return '';
    const classes = data.invoices.map(inv => this.getInvoiceStatusClass(inv));
    if (classes.includes('inv-open-overdue')) return 'inv-open-overdue';
    if (classes.includes('inv-open-future')) return 'inv-open-future';
    if (classes.includes('inv-ready') || classes.includes('inv-invoiced')) return 'inv-ready';
    if (classes.includes('inv-paid')) return 'inv-paid';
    return '';
  }

  /**
   * The month's TOTAL percentage across every milestone in that cell.
   * Previously this showed only `invoices[0]` with a '...' suffix, so a month holding
   * 20% + 30% read as "20%..." instead of "50%" - the figure users were reconciling against.
   * A '*' marks a multi-milestone cell; the tooltip lists the individual items.
   */
  getMonthPercentage(projectData: ProjectInvoiceData, month: number): string {
    const data = projectData.monthlyData[month];
    if (data && data.invoices.length > 0) {
      const total = `${data.totalPercentage.toFixed(0)}%`;
      return data.invoices.length > 1 ? `${total}*` : total;
    }
    return '-';
  }

  /** Full detail used in Excel export (all percentages) */
  private getMonthPercentageFull(projectData: ProjectInvoiceData, month: number): string {
    const data = projectData.monthlyData[month];
    if (data && data.invoices.length > 0) {
      return data.invoices.map(inv => `${inv.percentage.toFixed(0)}%`).join(' / ');
    }
    return '-';
  }

  getMonthTooltip(projectData: ProjectInvoiceData, month: number): string {
    const data = projectData.monthlyData[month];
    if (data && data.count > 0) {
      const invoiceList = data.invoices.map((inv, index) => {
        // '~' marks a milestone placed by its Invoiced Date because it has no Target Date,
        // so the month it sits in is traceable rather than appearing authoritative.
        const dateNote = this.isFallbackDated(inv) ? ' ~by invoiced date' : '';
        return `${index + 1}. ${inv.paymentMilestone}: ${inv.percentage}% [${inv.invoicingStatus}]${dateNote}`;
      }).join('\n');

      const total = `Total: ${data.totalPercentage.toFixed(0)}% (all statuses)`;

      // Billed subtotal alongside the planned total: the cell figure deliberately includes
      // Open and Ready for Invoice, which is right for a planning view but does not reconcile
      // with the project's Payment Tracking tab unless the billed share is shown too.
      const billed = data.invoices
        .filter(inv => ['invoiced', 'paid'].includes((inv.invoicingStatus ?? '').trim().toLowerCase()))
        .reduce((sum, inv) => sum + (inv.percentage ?? 0), 0);
      const billedLine = `Of which Invoiced + Paid: ${billed.toFixed(0)}%`;

      return `${projectData.project.name}\n${data.count} invoice(s)\n${invoiceList}\n${total}\n${billedLine}`;
    }
    return '';
  }

  /** Warning shown on rows holding milestones that have no date to place them by. */
  getUndatedWarning(projectData: ProjectInvoiceData): string {
    const n = projectData.undatedCount;
    if (!n) return '';
    return `${n} milestone${n === 1 ? '' : 's'} not shown — no Target Date or Invoiced Date. `
         + `Open the project's Payment Tracking tab to add one.`;
  }

  openCalendarDialog(month: number) {
    this.selectedMonthForCalendar = month;
    this.selectedYearForCalendar = this.selectedYear;
    this.buildCalendarDays();
    this.showCalendarDialog = true;
  }

  buildCalendarDays() {
    const year = this.selectedYearForCalendar;
    const month = this.selectedMonthForCalendar;
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDayOfWeek = firstDay.getDay();
    
    this.calendarDays = [];
    
    // Add empty days for the first week
    for (let i = 0; i < startingDayOfWeek; i++) {
      this.calendarDays.push({
        day: 0,
        date: new Date(year, month, 0),
        invoices: [],
        totalPercentage: 0,
        count: 0,
        status: 'none'
      });
    }
    
    // Add days of the month
    for (let day = 1; day <= daysInMonth; day++) {
      const date = new Date(year, month, day);
      const dayInvoices: PaymentTrackingItem[] = [];
      let totalPercentage = 0;
      
      // Find all invoices for this date across all projects
      this.projectInvoiceData.forEach(projectData => {
        projectData.invoices.forEach(invoice => {
          if (invoice.targetDate) {
            const targetDate = new Date(invoice.targetDate);
            if (
              targetDate.getFullYear() === year &&
              targetDate.getMonth() === month &&
              targetDate.getDate() === day
            ) {
              dayInvoices.push(invoice);
              totalPercentage += invoice.percentage;
            }
          }
        });
      });
      
      this.calendarDays.push({
        day,
        date,
        invoices: dayInvoices,
        totalPercentage,
        count: dayInvoices.length,
        status: dayInvoices.length > 0 ? 'green' : 'none'
      });
    }
  }

  getCalendarMonthName(): string {
    return this.months.find(m => m.value === this.selectedMonthForCalendar)?.fullName || '';
  }

  navigateCalendarMonth(direction: number) {
    this.selectedMonthForCalendar += direction;
    if (this.selectedMonthForCalendar < 0) {
      this.selectedMonthForCalendar = 11;
      this.selectedYearForCalendar--;
    } else if (this.selectedMonthForCalendar > 11) {
      this.selectedMonthForCalendar = 0;
      this.selectedYearForCalendar++;
    }
    this.buildCalendarDays();
  }

  getDayTooltip(day: CalendarDay): string {
    if (day.count === 0) return '';
    
    const projectInvoices = day.invoices.map((inv, index) => {
      const project = this.projects.find(p => p.id === inv.projectId);
      return `${index + 1}. ${project?.name || 'Unknown'}: ${inv.paymentMilestone} - ${inv.percentage}%`;
    }).join('\n');
    
    return `${day.count} invoice(s) (Total: ${day.totalPercentage.toFixed(0)}%)\n${projectInvoices}`;
  }

  /** Returns the dominant status CSS class for a calendar day cell. */
  getCalendarDayStatusClass(day: CalendarDay): string {
    if (day.count === 0) return '';
    const classes = day.invoices.map(inv => this.getInvoiceStatusClass(inv));
    if (classes.includes('inv-open-overdue')) return 'cal-overdue';
    if (classes.includes('inv-open-future')) return 'cal-open';
    if (classes.includes('inv-ready') || classes.includes('inv-invoiced')) return 'cal-ready';
    if (classes.includes('inv-paid')) return 'cal-paid';
    return 'cal-open';
  }

  onDateClick(day: CalendarDay) {
    if (day.count > 0) {
      this.selectedDate = day.date;
      // Navigate to payment tracking with filters
      const projectIds = [...new Set(day.invoices.map(inv => inv.projectId))];
      const dateStr = day.date.toISOString().split('T')[0];
      
      // Navigate to invoicing-plan route with query params
      this.router.navigate(['/invoicing-plan'], {
        queryParams: {
          date: dateStr,
          projects: projectIds.join(',')
        }
      });
      
      this.closeCalendarDialog();
    }
  }

  closeCalendarDialog() {
    this.showCalendarDialog = false;
    this.selectedDate = null;
  }

  isWeekend(date: Date): boolean {
    const day = date.getDay();
    return day === 0 || day === 6;
  }

  exportToExcel() {
    const data: any[] = [];
    
    // Header row - includes Project Name, Invoicing Type, all months, and a data-gap column.
    // The gap column travels with the export deliberately: a spreadsheet detached from the
    // screen's warning icon would otherwise read as complete.
    const headerRow = [
      'Project Name',
      'Invoicing Type',
      ...this.months.map(m => m.fullName),
      'Milestones Not Shown (no date)'
    ];
    data.push(headerRow);

    // Data rows
    this.filteredProjects.forEach(projectData => {
      const row = [
        projectData.project.name,
        projectData.invoiceTypeName || 'Monthly',
        ...this.months.map(m => this.getMonthPercentageFull(projectData, m.value)),
        projectData.undatedCount || ''
      ];
      data.push(row);
    });
    
    // Create workbook and worksheet
    const ws = XLSX.utils.aoa_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Invoice Planning');
    
    // Set column widths - Project Name (30), Invoicing Type (18), Months (12 each), gap col (28)
    const colWidths = [
      { wch: 30 }, // Project Name
      { wch: 18 }, // Invoicing Type
      ...this.months.map(() => ({ wch: 12 })), // Month columns
      { wch: 28 } // Milestones Not Shown
    ];
    ws['!cols'] = colWidths;
    
    // Export
    const excelBuffer = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
    const blob = new Blob([excelBuffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
    const fileName = `Invoice_Planning_${this.selectedYear}_${new Date().toISOString().split('T')[0]}.xlsx`;
    saveAs(blob, fileName);
    
    this.messageService.add({
      severity: 'success',
      summary: 'Success',
      detail: 'Invoice planning data exported to Excel successfully.'
    });
  }

  get totalProjectsCount(): number { return this.projectInvoiceData.length; }
  get showingCount(): number { return this.filteredProjects.length; }
  get monthlyCount(): number {
    return this.projectInvoiceData.filter(p => p.invoiceType === InvoiceType.Monthly).length;
  }
  get milestoneCount(): number {
    return this.projectInvoiceData.filter(p => p.invoiceType === InvoiceType.ProjectMilestone).length;
  }
}
